import React, { useEffect, useMemo, useState } from 'react';
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
  useIonToast,
} from '@ionic/react';

import dayjs from 'dayjs';

import PuntoServicioAPI from 'src/services/puntoDeServicio';
import LimpiezasService, { LimpiezaPlanificacion } from 'src/services/limpiezasProfundas.service';
import GestionMarcacionService from 'src/services/gestionMarcacion';

import { GestionErroresDetalleItem } from 'src/modules/gestionErrores/types';

/**
 * Modal para asignar un error SIN-ASIGNACION a una planificación de Limpieza Profunda.
 *
 * Reglas confirmadas:
 * - Operario: debe ser el mismo del error (no se elige).
 * - Horario: se toma de HE/HS en el detalle del error.
 * - Planificaciones disponibles: no finalizadas (codigo_estado != 'FIN') e “en proceso” (incluida).
 * - Al confirmar: crear asignación en LP y luego resetear el caso SIN-ASIGNACION para permitir reproceso automático.
 *   Solo cuando el reset es OK se marca el error como gestionado en UI (onAssigned).
 */

type Props = {
  isOpen: boolean;
  row: GestionErroresDetalleItem | null;
  onClose: () => void;
  onAssigned: (info: {
    referencia_id: string;
    ps_id: number;
    fecha: string;
    operario_legajo: number | null;
    lp_id: number;
  }) => void;
};

// Orden de visualización: priorizamos las frecuencias más comunes primero.
// Comentario: “otros” queda al final para no mezclar con las programadas.
const FRECUENCIA_ORDER = [
  'mensual',
  'quincenal',
  'bimestral',
  'trimestral',
  'cuatrimestral',
  'semestral',
  'anual',
  'otros',
] as const;

type FrecuenciaKey = (typeof FRECUENCIA_ORDER)[number];

type PlanDetalleCupo = {
  cupo_planificado_txt: string;
  cupo_planificado_min: number;
  total_asignado_txt: string;
  total_asignado_min: number;
  disponible_txt: string;
  disponible_min: number;
};

const _hhmmToMinutes = (value?: string | null): number => {
  const txt = String(value || '').trim();
  if (!txt) return 0;
  if (!txt.includes(':')) return 0;
  const [hh, mm] = txt.split(':');
  const h = Number(hh);
  const m = Number((mm || '').slice(0, 2));
  if (!Number.isFinite(h) || !Number.isFinite(m)) return 0;
  return h * 60 + m;
};

const _minutesToHHMM = (minutos: number): string => {
  const total = Math.max(0, Math.floor(Number(minutos) || 0));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
};

const _duracionMinutos = (horaInicio?: string, horaFin?: string): number => {
  const ini = _hhmmToMinutes(horaInicio);
  const fin = _hhmmToMinutes(horaFin);
  if (ini <= 0 && fin <= 0) return 0;
  if (fin >= ini) return fin - ini;
  // Caso cruza medianoche.
  return (24 * 60 - ini) + fin;
};

const _fechaISOFromFechaHora = (fechaHora: string): string => {
  // Normaliza distintas serializaciones de backend a YYYY-MM-DD.
  const txt = String(fechaHora || '').trim();
  if (!txt) return '';

  const m = txt.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];

  const d = dayjs(txt);
  return d.isValid() ? d.format('YYYY-MM-DD') : '';
};

const _extraerHorasDesdeDetalle = (detalle: string): { he?: string; hs?: string } => {
  // "detalle" viene del backend con patrón: "Hentrada=HH:MM Hsalida=HH:MM ...".
  const txt = String(detalle || '');
  const mEnt = txt.match(/Hentrada=([0-9]{2}:[0-9]{2})/i);
  const mSal = txt.match(/Hsalida=([0-9]{2}:[0-9]{2})/i);
  return {
    he: mEnt ? mEnt[1] : undefined,
    hs: mSal ? mSal[1] : undefined,
  };
};

const _toNumberOrNull = (value: any): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const txt = String(value ?? '').trim();
  if (!txt) return null;
  if (!/^[0-9]+$/.test(txt)) return null;
  const n = Number(txt);
  return Number.isFinite(n) ? n : null;
};

const AsignarLimpiezaProfundaModal: React.FC<Props> = ({ isOpen, row, onClose, onAssigned }) => {
  const [present] = useIonToast();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reprocesando, setReprocesando] = useState(false);

  // Control de idempotencia UX:
  // Si ya se creó la asignación en LP y falla el reset, bloqueamos volver a confirmar
  // para evitar duplicar asignaciones y ofrecemos “Reintentar reproceso”.
  const [asignacionCreada, setAsignacionCreada] = useState(false);
  const [pendienteReproceso, setPendienteReproceso] = useState(false);
  const [assignedInfo, setAssignedInfo] = useState<{
    referencia_id: string;
    ps_id: number;
    fecha: string;
    operario_legajo: number | null;
    lp_id: number;
  } | null>(null);

  const [planificaciones, setPlanificaciones] = useState<LimpiezaPlanificacion[]>([]);
  const [planSeleccionado, setPlanSeleccionado] = useState<LimpiezaPlanificacion | null>(null);

  // Cache de cupos por planificación (para mostrar disponibilidad y validar antes de guardar).
  const [cuposByPlanId, setCuposByPlanId] = useState<Record<number, PlanDetalleCupo | null>>({});

  const psId = Number(row?.ps_id);
  const psNombre = String(row?.ps_nombre || '').trim();

  const fechaISO = useMemo(() => _fechaISOFromFechaHora(String(row?.fecha_hora || '')), [row?.fecha_hora]);
  const { he, hs } = useMemo(() => _extraerHorasDesdeDetalle(String(row?.detalle || '')), [row?.detalle]);
  const legajo = useMemo(() => _toNumberOrNull(row?.operario_legajo), [row?.operario_legajo]);
  const duracionMin = useMemo(() => _duracionMinutos(he, hs), [he, hs]);
  const duracionTxt = useMemo(() => (duracionMin > 0 ? _minutesToHHMM(duracionMin) : ''), [duracionMin]);

  const mesAnio = useMemo(() => {
    if (!fechaISO) return null;
    const d = dayjs(fechaISO);
    if (!d.isValid()) return null;
    return { mes: d.month() + 1, anio: d.year() };
  }, [fechaISO]);

  const planificacionesNoFinalizadas = useMemo(() => {
    // Comentario:
    // Antes filtrábamos solo por “fecha = la del error”, pero EMSYS suele cargar LP el 01 del mes.
    // Por eso mostramos TODO el mes y solo excluimos finalizadas.
    return (planificaciones || []).filter((p) => {
      const codigo = String(p.codigo_estado || '').toUpperCase();
      return codigo !== 'FIN';
    });
  }, [planificaciones]);

  const planificacionesPorFrecuencia = useMemo(() => {
    const grupos: Record<string, LimpiezaPlanificacion[]> = {};
    for (const plan of planificacionesNoFinalizadas) {
      const key = String(plan.frecuencia_normalizada || 'otros').toLowerCase();
      if (!grupos[key]) grupos[key] = [];
      grupos[key].push(plan);
    }

    // Ordenamos cada grupo por fecha (más cercano al inicio del mes primero).
    Object.keys(grupos).forEach((k) => {
      grupos[k].sort((a, b) => {
        const fa = dayjs(a.fecha_iso || a.fecha);
        const fb = dayjs(b.fecha_iso || b.fecha);
        if (!fa.isValid() && !fb.isValid()) return 0;
        if (!fa.isValid()) return 1;
        if (!fb.isValid()) return -1;
        return fa.valueOf() - fb.valueOf();
      });
    });

    return grupos;
  }, [planificacionesNoFinalizadas]);

  const frecuenciaSecciones = useMemo(() => {
    // Comentario: solo devolvemos frecuencias con items, en el orden definido.
    const secciones: Array<{ key: FrecuenciaKey; label: string; items: LimpiezaPlanificacion[] }> = [];

    for (const key of FRECUENCIA_ORDER) {
      const items = (planificacionesPorFrecuencia[String(key)] || []) as LimpiezaPlanificacion[];
      if (!items || items.length === 0) continue;

      // Tomamos el label desde el primer item (viene del backend ya normalizado).
      const label = String(items[0]?.frecuencia_label || items[0]?.frecuencia || key);
      secciones.push({ key, label, items });
    }

    return secciones;
  }, [planificacionesPorFrecuencia]);

  useEffect(() => {
    if (!isOpen) return;

    // Reset visual.
    setPlanificaciones([]);
    setPlanSeleccionado(null);
    setCuposByPlanId({});
    setAsignacionCreada(false);
    setPendienteReproceso(false);
    setAssignedInfo(null);

    const cargar = async () => {
      if (!row) return;
      if (!Number.isFinite(psId) || psId <= 0) {
        present({ message: 'No se pudo identificar el PS del error.', duration: 3500, color: 'danger' });
        return;
      }
      if (!psNombre) {
        present({ message: 'No se pudo identificar el nombre del PS del error.', duration: 3500, color: 'danger' });
        return;
      }
      if (!fechaISO) {
        present({ message: 'No se pudo determinar la fecha del error.', duration: 3500, color: 'danger' });
        return;
      }
      if (!mesAnio) {
        present({ message: 'No se pudo determinar el mes/año del error.', duration: 3500, color: 'danger' });
        return;
      }

      try {
        setLoading(true);

        // El endpoint de LP exige ps_codigo y ps_nombre; desde Gestión de Errores solo tenemos ps_id y ps_nombre.
        // Por eso buscamos el PS por nombre y elegimos el que matchee por id.
        const psResp = await PuntoServicioAPI.listar(psNombre);
        const lista = (psResp?.data?.datos || []) as Array<any>;
        const match = lista.find((p) => Number(p?.id) === psId);
        const psCodigo = String(match?.ps_codigo || '').trim();
        const psNombreReal = String(match?.ps_nombre || psNombre).trim();

        if (!psCodigo) {
          present({
            message: 'No se pudo obtener el código del PS para cargar Limpiezas Profundas.',
            duration: 3800,
            color: 'danger',
          });
          return;
        }

        const payload = await LimpiezasService.obtenerPlanificaciones({
          psId,
          psCodigo,
          psNombre: psNombreReal,
          mes: mesAnio.mes,
          anio: mesAnio.anio,
        });

        setPlanificaciones(payload?.planificaciones || []);
      } catch (e: any) {
        const msg = e?.response?.data?.error || 'No se pudieron cargar las planificaciones de LP.';
        present({ message: String(msg), duration: 3800, color: 'danger' });
      } finally {
        setLoading(false);
      }
    };

    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    // Precargamos cupo/asignado por planificación para mostrar “Horas disponibles”.
    // Comentario: el endpoint de detalle aplica permisos; si no autoriza o falla, dejamos null y no bloqueamos por UI.
    if (!isOpen) return;
    const lista = planificacionesNoFinalizadas || [];
    if (lista.length === 0) return;

    let cancelled = false;

    const cargarCupos = async () => {
      // Limitar concurrencia para no saturar la API.
      const concurrency = 4;
      const queue = [...lista];

      const worker = async () => {
        while (queue.length > 0) {
          const item = queue.shift();
          if (!item) return;
          const id = Number(item.id);
          if (!Number.isFinite(id) || id <= 0) continue;
          if (cuposByPlanId[id] !== undefined) continue;

          try {
            const resp = await LimpiezasService.fetchPlanificacionDetalle(id);
            const plan = resp?.planificacion as any;

            const cupoPlanTxt = String(plan?.cupo_planificado || item.cant_horas || '0:00');
            const cupoPlanMin = Number(plan?.cupo_planificado_min ?? _hhmmToMinutes(cupoPlanTxt));

            const totalAsignadoTxt = String(plan?.total_asignado || plan?.cupo_utilizada || '0:00');
            const totalAsignadoMin = _hhmmToMinutes(totalAsignadoTxt);

            const disponibleMin = Math.max(cupoPlanMin - totalAsignadoMin, 0);
            const cupo: PlanDetalleCupo = {
              cupo_planificado_txt: cupoPlanTxt,
              cupo_planificado_min: cupoPlanMin,
              total_asignado_txt: totalAsignadoTxt,
              total_asignado_min: totalAsignadoMin,
              disponible_txt: _minutesToHHMM(disponibleMin),
              disponible_min: disponibleMin,
            };

            if (!cancelled) {
              setCuposByPlanId((prev) => ({ ...prev, [id]: cupo }));
            }
          } catch {
            if (!cancelled) {
              setCuposByPlanId((prev) => ({ ...prev, [id]: null }));
            }
          }
        }
      };

      await Promise.all(Array.from({ length: Math.min(concurrency, lista.length) }, () => worker()));
    };

    cargarCupos();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, planificacionesNoFinalizadas]);

  const onConfirmar = async () => {
    if (!row) return;
    if (asignacionCreada) return;
    if (!planSeleccionado) {
      present({ message: 'Debe seleccionar una planificación de LP.', duration: 2800, color: 'warning' });
      return;
    }
    if (!fechaISO) {
      present({ message: 'No se pudo determinar la fecha del error.', duration: 3200, color: 'danger' });
      return;
    }
    if (!he || !hs) {
      present({ message: 'El error no trae HE/HS para asignar a LP.', duration: 3400, color: 'danger' });
      return;
    }
    if (duracionMin <= 0) {
      present({ message: 'El horario (HE/HS) del error no es válido.', duration: 3400, color: 'danger' });
      return;
    }
    if (legajo == null) {
      present({ message: 'No se pudo identificar el legajo del operario.', duration: 3400, color: 'danger' });
      return;
    }

    try {
      setSaving(true);

      // Validación preventiva de cupo disponible (si tenemos el dato precargado).
      // Comentario: el backend también valida cupo; esto solo mejora UX.
      const cupo = cuposByPlanId[planSeleccionado.id];
      if (cupo && cupo.disponible_min >= 0 && duracionMin > cupo.disponible_min) {
        present({
          message: `El horario del error (${duracionTxt}) supera el disponible (${cupo.disponible_txt}) de la planificación seleccionada.`,
          duration: 4800,
          color: 'warning',
        });
        return;
      }

      // Resolver operario_id desde LP usando el legajo (el operario debe ser el mismo del error).
      const disponibles = await LimpiezasService.buscarOperarios(psId, String(legajo));
      const encontrado = (disponibles || []).find((o) => String(o.legajo) === String(legajo));

      if (!encontrado?.id) {
        present({
          message: 'No se encontró el operario como disponible para LP en ese PS.',
          duration: 4000,
          color: 'danger',
        });
        return;
      }

      await LimpiezasService.crearAsignacion(planSeleccionado.id, {
        operario_id: Number(encontrado.id),
        fecha_ejecucion: fechaISO,
        hora_inicio: he,
        hora_fin: hs,
      });

      // Guardamos el payload para un posible reintento de reproceso, sin duplicar la asignación.
      const info = {
        referencia_id: String(row.referencia_id || '').trim(),
        ps_id: Number(row.ps_id),
        fecha: fechaISO,
        operario_legajo: legajo,
        lp_id: planSeleccionado.id,
      };
      setAssignedInfo(info);
      setAsignacionCreada(true);

      // Reproceso acotado (Opción A): reset SIN-ASIGNACION (Estado=NULL + borrar HNP).
      // Si esto falla, NO marcamos gestionado: dejamos reintento disponible para no ocultar el problema.
      const referenciaIdNum = Number(info.referencia_id);
      if (!Number.isFinite(referenciaIdNum) || referenciaIdNum <= 0) {
        setPendienteReproceso(true);
        present({
          message: 'Asignación creada, pero no se pudo reprocesar (referencia inválida). Puede reintentar el reproceso.',
          duration: 5200,
          color: 'warning',
        });
        return;
      }

      try {
        await GestionMarcacionService.resetearSinAsignacion(referenciaIdNum);
      } catch (e: any) {
        const msg = e?.response?.data?.error || 'Asignación creada, pero el reproceso falló. Puede reintentar.';
        setPendienteReproceso(true);
        present({ message: String(msg), duration: 5200, color: 'warning' });
        return;
      }

      // Notificar al padre para:
      // - marcar gestionado localmente
      // - descontar del contador en el resumen
      onAssigned(info);

      present({ message: 'Asignación creada y reproceso habilitado.', duration: 2500, color: 'success' });
      onClose();
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'No se pudo crear la asignación en LP.';
      present({ message: String(msg), duration: 4200, color: 'danger' });
    } finally {
      setSaving(false);
    }
  };

  const onReintentarReproceso = async () => {
    if (!assignedInfo) {
      present({ message: 'No hay información para reprocesar. Vuelva a abrir el modal.', duration: 3200, color: 'danger' });
      return;
    }

    const referenciaIdNum = Number(assignedInfo.referencia_id);
    if (!Number.isFinite(referenciaIdNum) || referenciaIdNum <= 0) {
      present({ message: 'No se pudo reprocesar (referencia inválida).', duration: 3800, color: 'danger' });
      return;
    }

    try {
      setReprocesando(true);
      await GestionMarcacionService.resetearSinAsignacion(referenciaIdNum);
      setPendienteReproceso(false);
      onAssigned(assignedInfo);
      present({ message: 'Reproceso completado. El error quedará gestionado.', duration: 2800, color: 'success' });
      onClose();
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'No se pudo reprocesar. Intente nuevamente.';
      present({ message: String(msg), duration: 4200, color: 'warning' });
    } finally {
      setReprocesando(false);
    }
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar color="primary">
          <IonTitle>Asignar a Limpieza Profunda</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose} disabled={saving}>
              Cerrar
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent style={{ padding: 12, '--background': '#F6F7F9' } as any}>
        <IonCard
          style={{
            margin: 0,
            marginBottom: 10,
            borderRadius: 12,
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
            overflow: 'hidden',
          }}
        >
          <IonCardContent style={{ paddingTop: 12, paddingBottom: 12, background: '#FFFFFF' }}>
            <div style={{ textAlign: 'center', color: '#111', fontSize: 13, lineHeight: 1.45 }}>
              <div>
                <span style={{ fontWeight: 700 }}>PS:</span> <span style={{ fontWeight: 800 }}>{psNombre || '-'}</span>
              </div>
              <div>
                <span style={{ fontWeight: 700 }}>Fecha:</span> <span style={{ fontWeight: 800 }}>{fechaISO || '-'}</span>
              </div>
              <div>
                <span style={{ fontWeight: 700 }}>Mes LP:</span>{' '}
                <span style={{ fontWeight: 800 }}>
                  {mesAnio ? `${String(mesAnio.mes).padStart(2, '0')}/${mesAnio.anio}` : '-'}
                </span>
              </div>
              <div>
                <span style={{ fontWeight: 700 }}>Horario:</span>{' '}
                <span style={{ fontWeight: 800 }}>{he || '--:--'}</span> a <span style={{ fontWeight: 800 }}>{hs || '--:--'}</span>
                {duracionTxt ? <span style={{ fontWeight: 700 }}> ({duracionTxt})</span> : null}
              </div>
              <div>
                <span style={{ fontWeight: 700 }}>Operario:</span>{' '}
                <span style={{ fontWeight: 800 }}>{row?.operario_nombre || 'Operario'}</span> (Legajo {row?.operario_legajo ?? '-'})
              </div>
            </div>
          </IonCardContent>
        </IonCard>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
            <IonSpinner />
          </div>
        ) : null}

        {!loading && frecuenciaSecciones.length === 0 ? (
          <IonText color="medium" style={{ display: 'block', marginTop: 14 }}>
            No hay planificaciones de LP disponibles para este PS en el mes.
          </IonText>
        ) : null}

        {!loading && frecuenciaSecciones.length > 0 ? (
          <IonList>
            {frecuenciaSecciones.map((sec) => (
              <React.Fragment key={sec.key}>
                <IonItem lines="full" color="light">
                  <IonLabel>
                    <div style={{ fontWeight: 800 }}>{sec.label}</div>
                    <div style={{ fontSize: 12, color: '#666' }}>{sec.items.length} planificación(es)</div>
                  </IonLabel>
                </IonItem>

                {sec.items.map((p) => {
                  const selected = planSeleccionado?.id === p.id;
                  const fechaPlan = (() => {
                    try {
                      const d = dayjs(p.fecha_iso || p.fecha);
                      return d.isValid() ? d.format('DD/MM/YYYY') : String(p.fecha || '-');
                    } catch {
                      return String(p.fecha || '-');
                    }
                  })();

                  const cupo = cuposByPlanId[p.id];
                  const horasTxt = cupo ? `Disponible: ${cupo.disponible_txt}` : 'Disponible: -';

                  // Comentario: color="light" queda muy sutil (gris) en móvil.
                  // Usamos variables CSS de Ionic para que el fondo se aplique correctamente en IonItem.
                  const selectedStyle = (
                    selected
                      ? {
                          '--background': '#c2f0cf',
                          '--background-activated': '#c2f0cf',
                          '--background-focused': '#c2f0cf',
                          borderLeft: '4px solid #0b7c11',
                        }
                      : { borderLeft: '2px solid transparent' }
                  ) as any;

                  return (
                    <IonItem
                      key={p.id}
                      button={!asignacionCreada}
                      disabled={asignacionCreada}
                      onClick={() => {
                        if (asignacionCreada) return;
                        setPlanSeleccionado(p);
                      }}
                      style={selectedStyle}
                    >
                      <IonLabel>
                        <div style={{ fontWeight: 700 }}>{p.tipo_lavado || 'Limpieza Profunda'}</div>
                        <div style={{ fontSize: 12, color: '#666' }}>
                          Fecha plan: {fechaPlan} | Estado: {p.estado || '-'} | {horasTxt}
                        </div>
                      </IonLabel>
                    </IonItem>
                  );
                })}
              </React.Fragment>
            ))}
          </IonList>
        ) : null}

        <div style={{ marginTop: 12 }}>
          {pendienteReproceso ? (
            <IonText color="warning" style={{ display: 'block', marginBottom: 10 }}>
              Se creó la asignación en LP, pero falta el reproceso para que el caso deje de aparecer como SIN-ASIGNAR.
              Use “Reintentar reproceso”.
            </IonText>
          ) : null}

          <IonButton
            expand="block"
            onClick={onConfirmar}
            disabled={saving || reprocesando || loading || frecuenciaSecciones.length === 0 || asignacionCreada}
          >
            {saving ? 'Guardando...' : asignacionCreada ? 'Asignación creada' : 'Confirmar asignación'}
          </IonButton>

          {pendienteReproceso ? (
            <IonButton
              expand="block"
              color="tertiary"
              onClick={onReintentarReproceso}
              disabled={reprocesando || saving}
              style={{ marginTop: 10 }}
            >
              {reprocesando ? 'Reprocesando...' : 'Reintentar reproceso'}
            </IonButton>
          ) : null}
        </div>
      </IonContent>
    </IonModal>
  );
};

export default AsignarLimpiezaProfundaModal;
